import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Flame } from "lucide-react";

const STATUS = ["All", "Upcoming", "Live", "Listed"];
const TYPES = ["All", "Mainboard", "SME"];
const STATUS_COLOR = {
  "Upcoming": "bg-cyan-500/15 text-cyan-700 border-cyan-500/30",
  "Live": "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  "Listed": "bg-slate-500/15 text-slate-700 border-slate-500/30",
};
const EX_COLOR = {
  "NSE": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "BSE": "bg-orange-50 text-orange-700 border-orange-200",
  "NSE-SME": "bg-purple-50 text-purple-700 border-purple-200",
  "BSE-SME": "bg-pink-50 text-pink-700 border-pink-200",
};

function IPOCard({ i }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5 hover:border-[#D91E2A]/40 transition-all card-glow" data-testid={`ipo-full-card-${i.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="font-heading font-bold text-lg text-slate-900">{i.company_name}</h3>
          <div className="text-xs text-slate-500 font-mono-fin uppercase mt-0.5">{i.sector}</div>
        </div>
        <div className="flex flex-wrap gap-1 items-center">
          {i.exchange && (
            <span className={`text-[9px] font-mono-fin uppercase tracking-wider px-2 py-1 rounded border ${EX_COLOR[i.exchange] || EX_COLOR.NSE}`}>{i.exchange}</span>
          )}
          {i.ipo_type && i.ipo_type !== "Mainboard" && (
            <span className="text-[9px] font-mono-fin uppercase tracking-wider px-2 py-1 rounded border bg-amber-50 text-amber-700 border-amber-200">{i.ipo_type}</span>
          )}
          <span className={`text-[10px] font-mono-fin uppercase tracking-wider px-2 py-1 rounded border ${STATUS_COLOR[i.status] || STATUS_COLOR.Listed}`}>{i.status}</span>
        </div>
      </div>
      {i.description && <p className="text-sm text-slate-600 mb-4 leading-relaxed">{i.description}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric label="Price Band" value={i.price_band || "TBA"} />
        <Metric label="Issue Size" value={i.issue_size || "TBA"} />
        <Metric label="GMP" value={i.gmp || "-"} tone={(i.gmp && i.gmp !== "N/A") ? ((i.gmp_value || 0) >= 0 ? "green" : "red") : undefined} />
        <Metric label="Subscription" value={i.subscription || "-"} />
        <Metric label="Open" value={i.open_date || "-"} />
        <Metric label="Close" value={i.close_date || "-"} />
        <Metric label="Listing" value={i.listing_date || "-"} />
        <Metric label="Lot Size" value={i.lot_size || "-"} />
      </div>
    </div>
  );
}

function Metric({ label, value, tone }) {
  const toneCls = tone === "green" ? "text-[#16A34A]" : tone === "red" ? "text-[#DC2626]" : tone === "emerald" ? "text-emerald-600" : "text-slate-900";
  return (
    <div>
      <div className="text-[9px] font-mono-fin uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono-fin text-sm ${toneCls}`}>{value}</div>
    </div>
  );
}

export default function IPOHub() {
  const [tab, setTab] = useState("All");
  const [type, setType] = useState("All");
  const { data } = useQuery({
    queryKey: ["ipos"],
    queryFn: async () => (await api.get("/ipos")).data,
    refetchInterval: 5 * 60_000,
  });
  const items = (data?.data || [])
    .filter(i => tab === "All" || i.status === tab)
    .filter(i => type === "All" || (i.ipo_type || "Mainboard") === type);

  const counts = { All: 0, Mainboard: 0, SME: 0 };
  (data?.data || []).forEach(i => { counts.All += 1; counts[(i.ipo_type || "Mainboard")] = (counts[(i.ipo_type || "Mainboard")] || 0) + 1; });

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 max-w-[1300px] mx-auto">
      <div className="ns-eyebrow mb-2">IPO Intelligence</div>
      <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A] mb-2">IPO Hub</h1>
      <p className="text-[#6B6C6F] mb-6 max-w-2xl text-sm">Every upcoming and live public offering on NSE, BSE and their SME platforms — track price band, GMP, subscription, and listing dates in one place.</p>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="ipo-type-filter">
        {TYPES.map(t => (
          <button key={t} onClick={() => setType(t)} data-testid={`ipo-type-${t.toLowerCase()}`}
            className={`text-xs px-3 py-1.5 rounded-md font-semibold border transition-all ${type === t ? "bg-[#D91E2A]/10 text-[#D91E2A] border-[#D91E2A]/30" : "bg-white text-slate-700 border-slate-200 hover:border-[#D91E2A]/40"}`}>
            {t} <span className="opacity-60 ml-1">({counts[t] ?? 0})</span>
          </button>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200" data-testid="ipo-tabs">
          {STATUS.map(s => <TabsTrigger key={s} value={s} data-testid={`ipo-tab-${s.toLowerCase()}`}>{s}</TabsTrigger>)}
        </TabsList>
        <TabsContent value={tab} className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {items.map(i => <IPOCard key={i.id} i={i} />)}
          </div>
          {items.length === 0 && <div className="text-slate-500 font-mono-fin py-10 text-center">No IPOs match these filters.</div>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
